import React from 'react';
import { Lock, Eye, Shield, AlertCircle, Cookie, Mail } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Lock className="h-16 w-16 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Gizlilik Politikası
            </h1>
            <p className="text-xl text-green-100">
              Verilerinizin Gizliliği ve Güvenliği Hakkında Bilgiler
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-4 mb-6">
              <Shield className="h-8 w-8 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Giriş</h2>
                <p className="text-gray-600 leading-relaxed mb-3">
                  Pekala olarak, kullanıcılarımızın gizliliğine saygı duyuyor ve kişisel verilerinizi korumak için 
                  gereken tüm önlemleri alıyoruz. Bu gizlilik politikası, web sitemizi ziyaret ettiğinizde ve 
                  ürünlerimizi satın aldığınızda kişisel bilgilerinizin nasıl toplandığı, kullanıldığı ve korunduğu 
                  hakkında sizi bilgilendirmek amacıyla hazırlanmıştır.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Bu politikayı kabul etmekle, verilerinizin burada açıklandığı şekilde işlenmesine onay vermiş olursunuz.
                </p>
              </div>
            </div>
          </div>

          {/* Information We Collect */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-4 mb-4">
              <Eye className="h-8 w-8 text-green-600 flex-shrink-0 mt-1" />
              <h2 className="text-2xl font-bold text-gray-800">Topladığımız Bilgiler</h2>
            </div>
            
            <div className="space-y-6">
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-bold text-gray-800 mb-2">1. Kişisel Bilgiler</h3>
                <p className="text-gray-600 mb-2">
                  Web sitemizi kullanırken veya sipariş verirken aşağıdaki bilgileri toplayabiliriz:
                </p>
                <ul className="space-y-1 text-gray-600 ml-4">
                  <li>• Ad ve soyad</li>
                  <li>• E-posta adresi</li>
                  <li>• Telefon numarası</li>
                  <li>• Teslimat adresi</li>
                  <li>• Fatura bilgileri</li>
                </ul>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-bold text-gray-800 mb-2">2. Otomatik Toplanan Bilgiler</h3>
                <p className="text-gray-600 mb-2">
                  Web sitemizi ziyaret ettiğinizde otomatik olarak toplanan teknik bilgiler:
                </p>
                <ul className="space-y-1 text-gray-600 ml-4">
                  <li>• IP adresi</li>
                  <li>• Tarayıcı türü ve versiyonu</li>
                  <li>• İşletim sistemi</li>
                  <li>• Ziyaret edilen sayfalar</li>
                  <li>• Ziyaret tarihi ve saati</li>
                  <li>• Yönlendirme URL'si</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How We Use Information */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Bilgileri Nasıl Kullanırız?</h2>
            <div className="space-y-4">
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-3">Topladığımız bilgileri şu amaçlarla kullanırız:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span className="text-gray-700">Siparişlerinizi işlemek ve teslim etmek</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span className="text-gray-700">Müşteri hizmetleri sağlamak</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span className="text-gray-700">Ödeme işlemlerini gerçekleştirmek</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span className="text-gray-700">Kampanya ve duyurular göndermek</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span className="text-gray-700">Web sitesini geliştirmek</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span className="text-gray-700">Güvenlik önlemleri almak</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span className="text-gray-700">Yasal yükümlülükleri yerine getirmek</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span className="text-gray-700">Kullanıcı deneyimini iyileştirmek</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cookies */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-4 mb-4">
              <Cookie className="h-8 w-8 text-green-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Çerezler (Cookies)</h2>
                <p className="text-gray-600 mb-4">
                  Web sitemiz, kullanıcı deneyimini iyileştirmek ve site işlevselliğini sağlamak için çerezler kullanmaktadır.
                </p>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-1">Zorunlu Çerezler</h4>
                    <p className="text-sm text-gray-600">Web sitesinin düzgün çalışması için gerekli olan çerezlerdir.</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-1">Performans Çerezleri</h4>
                    <p className="text-sm text-gray-600">Ziyaretçilerin siteyi nasıl kullandığını anlamamıza yardımcı olur.</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-1">İşlevsellik Çerezleri</h4>
                    <p className="text-sm text-gray-600">Tercihlerinizi hatırlayarak kişiselleştirilmiş bir deneyim sunar.</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Tarayıcınızın ayarlarından çerezleri devre dışı bırakabilirsiniz, ancak bu durumda sitenin bazı özellikleri çalışmayabilir.
                </p>
              </div>
            </div>
          </div>

          {/* Data Security */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-4">
              <Shield className="h-8 w-8 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Veri Güvenliği</h2>
                <p className="text-gray-600 mb-4">
                  Kişisel verilerinizin güvenliğini sağlamak için aşağıdaki önlemleri alıyoruz:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600">🔒</span>
                    <span className="text-gray-700">SSL şifreleme teknolojisi</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600">🔒</span>
                    <span className="text-gray-700">Güvenli veri merkezleri</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600">🔒</span>
                    <span className="text-gray-700">Düzenli güvenlik denetimleri</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600">🔒</span>
                    <span className="text-gray-700">Erişim kontrolü ve yetkilendirme</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600">🔒</span>
                    <span className="text-gray-700">Güvenlik duvarı koruması</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-green-600">🔒</span>
                    <span className="text-gray-700">Veri yedekleme sistemleri</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Third Party Sharing */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-4">
              <AlertCircle className="h-8 w-8 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-3">Üçüncü Taraflarla Paylaşım</h2>
                <p className="text-gray-600 mb-4">
                  Kişisel bilgilerinizi, açık rızanız olmadan üçüncü taraflarla satmıyor, kiralamıyor veya ticari amaçlarla paylaşmıyoruz.
                </p>
                <p className="text-gray-600 mb-3">
                  Ancak aşağıdaki durumlarda verileriniz paylaşılabilir:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>• Hizmet sağlayıcılar (kargo, ödeme sistemleri) ile siparişinizi teslim etmek için</li>
                  <li>• Yasal zorunluluklar kapsamında yetkili mercilerle</li>
                  <li>• Açık rızanızın bulunduğu durumlarda</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Haklarınız</h2>
            <div className="bg-green-50 rounded-lg p-6">
              <p className="text-gray-700 mb-3 font-semibold">Kişisel verilerinizle ilgili olarak:</p>
              <div className="space-y-2 text-gray-700">
                <p>✓ Verilerinize erişim talep edebilirsiniz</p>
                <p>✓ Verilerinizin düzeltilmesini isteyebilirsiniz</p>
                <p>✓ Verilerinizin silinmesini talep edebilirsiniz</p>
                <p>✓ Veri işleme faaliyetlerine itiraz edebilirsiniz</p>
                <p>✓ Verilerinizin taşınabilirliğini talep edebilirsiniz</p>
                <p>✓ Pazarlama e-postalarından aboneliğinizi iptal edebilirsiniz</p>
              </div>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Çocukların Gizliliği</h2>
            <p className="text-gray-600">
              Web sitemiz 18 yaş altındaki kişilere yönelik değildir. Bilerek 18 yaş altındaki bireylerden kişisel 
              bilgi toplamıyoruz. Bir ebeveyn veya vasi olarak, çocuğunuzun rızası olmadan kişisel bilgi sağladığını 
              düşünüyorsanız, lütfen bizimle iletişime geçin.
            </p>
          </div>

          {/* Policy Changes */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Politika Değişiklikleri</h2>
            <p className="text-gray-600">
              Bu gizlilik politikasını zaman zaman güncelleyebiliriz. Değişiklikler bu sayfada yayınlanacak ve 
              önemli değişiklikler için size bildirim gönderilebilir. Düzenli olarak bu sayfayı kontrol etmenizi öneririz.
            </p>
          </div>

          {/* Contact */}
          <div className="bg-gradient-to-r from-green-600 to-green-800 text-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-4">
              <Mail className="h-8 w-8 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold mb-3">İletişim</h2>
                <p className="mb-4 text-green-100">
                  Gizlilik politikamız hakkında sorularınız veya endişeleriniz varsa, lütfen bizimle iletişime geçin:
                </p>
                <div className="space-y-2 text-green-100">
                  <p><strong>E-posta:</strong> pekala_sut_urunleri@hotmail.com</p>
                  <p><strong>Telefon:</strong> 0543 202 63 63</p>
                  <p><strong>Adres:</strong> Koçören 2. OSB Mh. 222. Cd. No:4, Eyyubiye/ŞANLIURFA</p>
                </div>
              </div>
            </div>
          </div>

          {/* Update Date */}
          <div className="text-center text-gray-500 text-sm">
            <p>Son Güncelleme: {new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p className="mt-2 text-xs">Bu gizlilik politikası, Pekala web sitesi kullanımı için geçerlidir.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
